import { useCallback } from 'react'
import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  Path,
  useWatch,
} from 'react-hook-form'
import { View } from 'react-native'
import { useTheme } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import FosterMedicationItemLarge from '@/components/fosters/cards/medications/FosterMedicationItemLarge'
import FosterMedicationItemSmall from '@/components/fosters/cards/medications/FosterMedicationItemSmall'
import Alert from '@/components/shared/Alert'
import UniIcon from '@/components/unistyles/UniIcon'
import UniIconButton from '@/components/unistyles/UniIconButton'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Theme } from '@/styles/theme'
import formatDate from '@/utils/formatDate'

interface Props<C extends FieldValues, _E extends FieldErrors> {
  control: Control<C, any>
  disabled: boolean
  expanded: boolean
  index: number
  formState: FormState<C>
  setExpanded: (expanded: boolean) => void
  onRemove: (index: number) => void
}

const FosterMedicationItem = <C extends FieldValues, E extends FieldErrors>({
  control,
  disabled,
  expanded,
  index,
  formState,
  setExpanded,
  onRemove,
}: Props<C, E>) => {
  const theme = useTheme<Theme>()
  const { isSmallScreen } = useBreakpoint()

  const medication = useWatch({
    name: `medications.${index}` as Path<C>,
    control,
  })

  const species = useWatch({
    name: `species` as Path<C>,
    control,
  })

  const handleRemove = useCallback(() => {
    Alert.alert('Delete', 'Are you sure you want to delete this medication?', [
      {
        text: 'Cancel',
        onPress: () => console.log('Cancel Pressed'),
        style: 'cancel',
      },
      {
        isPreferred: false,
        text: 'Delete',
        onPress: () => onRemove(index),
        style: 'destructive',
      },
    ])
  }, [onRemove, index])

  const buildSummaryDetail = useCallback(() => {
    const parts: string[] = []
    if (medication?.givenDate) {
      parts.push(`Given ${formatDate(medication.givenDate)}`)
    } else if (medication?.dueDate) {
      parts.push(`Due ${formatDate(medication.dueDate)}`)
    }
    if (medication?.givenBy) {
      parts.push(`by ${medication.givenBy}`)
    }
    if (medication?.isRepeating && medication?.frequency) {
      parts.push(`repeats every ${medication.frequency} days`)
    }
    return parts.join(' · ')
  }, [
    medication?.givenDate,
    medication?.dueDate,
    medication?.givenBy,
    medication?.isRepeating,
    medication?.frequency,
  ])

  if (!isSmallScreen) {
    const summaryDetail = buildSummaryDetail()

    return (
      <View style={styles.wideCard}>
        <UniTouchableRipple
          accessibilityRole="button"
          borderless
          onPress={() => setExpanded(!expanded)}
          style={styles.wideSummaryPressable}
        >
          <View style={styles.wideSummaryRow}>
            <View style={styles.wideStatusBadge(!!medication?.givenDate)}>
              {medication?.givenDate ? (
                <UniIcon color={'white'} size={18} source="check" />
              ) : (
                <UniIcon color={theme.colors.primary} size={18} source="pill" />
              )}
            </View>
            <View style={styles.wideSummaryText}>
              <UniText variant="titleSmall">
                {medication?.name || 'New Medication'}
              </UniText>
              {summaryDetail.length > 0 && (
                <UniText style={styles.wideSummaryDetail} variant="bodySmall">
                  {summaryDetail}
                </UniText>
              )}
            </View>
            <UniIcon
              size={24}
              source={expanded ? 'chevron-up' : 'chevron-down'}
            />
            <UniIconButton
              disabled={disabled}
              icon={'trash-can'}
              onPress={handleRemove}
              size={20}
            />
          </View>
        </UniTouchableRipple>

        <FosterMedicationItemLarge
          control={control}
          disabled={disabled}
          formState={formState}
          index={index}
          medication={medication}
          open={expanded}
          species={species}
        />
      </View>
    )
  }

  return (
    <View style={styles.mobileItemContainer}>
      <View style={styles.mobileRow}>
        <View style={styles.mobileStatusIcon}>
          {medication?.givenDate ? (
            <UniIcon color={'green'} size={24} source="check" />
          ) : (
            <UniIcon color={theme.colors.subtitle} size={24} source="pill" />
          )}
        </View>
        <View style={styles.mobileFieldsContainer}>
          <View>
            <UniTouchableRipple
              accessibilityRole="button"
              borderless
              onPress={() => {
                setExpanded(!expanded)
              }}
              style={styles.pressableContainer}
            >
              <View style={styles.mobileSummary}>
                <UniText>
                  {medication?.name}{' '}
                  {medication?.givenDate &&
                    `given ${formatDate(medication.givenDate)}`}
                  {medication?.dueDate &&
                    !medication.givenDate &&
                    `due ${formatDate(medication.dueDate)}`}
                </UniText>
                {medication?.givenBy && (
                  <UniText style={styles.clinicTitle}>
                    by {medication?.givenBy}
                  </UniText>
                )}
              </View>
            </UniTouchableRipple>
            <FosterMedicationItemSmall
              control={control}
              disabled={disabled}
              formState={formState}
              index={index}
              medication={medication}
              open={expanded}
              species={species}
            />
            {expanded && (
              <UniTouchableRipple
                accessibilityRole="button"
                borderless
                onPress={() => setExpanded(false)}
                style={styles.collapseContainer}
              >
                <UniIcon size={32} source={'chevron-up'} />
              </UniTouchableRipple>
            )}
          </View>
        </View>
        <View style={styles.mobileDeleteIcon}>
          <UniIconButton
            disabled={disabled}
            icon={'trash-can'}
            onPress={handleRemove}
          />
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    // ── Mobile styles ──
    clinicTitle: {
      color: theme.colors.subtitle,
      paddingTop: theme.tokens.spacing[1],
    },
    collapseContainer: {
      alignItems: 'center',
      borderRadius: theme.tokens.containerBorderRadius,
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: theme.tokens.spacing[3],
      padding: theme.tokens.spacing[2],
    },
    mobileDeleteIcon: {
      position: 'absolute',
      right: 0,
      top: 0,
    },
    mobileFieldsContainer: {
      flex: 1,
      flexDirection: 'column',
      gap: 4,
      height: '100%',
    },
    mobileItemContainer: {
      borderColor: theme.colors.outline,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 1,
      marginTop: theme.tokens.spacing[3],
    },
    mobileRow: {
      alignItems: 'center',
      flexDirection: 'row',
      flex: 1,
      gap: 4,
    },
    mobileStatusIcon: {
      left: 0,
      paddingHorizontal: 12,
      paddingVertical: 14,
      position: 'absolute',
      top: 0,
    },
    mobileSummary: {
      flex: 1,
      justifyContent: 'center',
      marginLeft: theme.tokens.spacing[8],
    },
    pressableContainer: {
      borderRadius: theme.tokens.containerBorderRadius,
      flex: 1,
      minHeight: 52,
      paddingHorizontal: theme.tokens.spacing[1],
      paddingVertical: theme.tokens.spacing[3],
    },

    // ── Wide styles ──
    wideCard: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.outlineVariant,
      borderRadius: 12,
      borderWidth: 1,
      marginTop: theme.tokens.spacing[3],
      overflow: 'hidden',
    },
    wideStatusBadge: (isGiven: boolean) => ({
      alignItems: 'center' as const,
      backgroundColor: isGiven ? 'green' : theme.colors.primaryContainer,
      borderRadius: 20,
      flexShrink: 0,
      height: 36,
      justifyContent: 'center' as const,
      width: 36,
    }),
    wideSummaryDetail: {
      color: theme.colors.subtitle,
      marginTop: 2,
    },
    wideSummaryPressable: {
      borderRadius: 12,
    },
    wideSummaryRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.tokens.spacing[3],
      paddingHorizontal: theme.tokens.spacing[5],
      paddingVertical: theme.tokens.spacing[4],
    },
    wideSummaryText: {
      flex: 1,
    },
  }
})

export default FosterMedicationItem
